import { UUID } from "@bitory-gmbh/nidavellir";
import { documentDbPut } from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Query,
  useDocumentDBGetAll,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import {
  useLazySamplesLaboratoryListQuery,
  useSamplesLaboratoryListQuery,
} from "@/services/backend/samples/api/lab-api";
import { Laboratory } from "@/services/backend/samples/types/laboratory";
import { useParams } from "react-router";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

export function useLaboratoriesOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesLaboratoryListQuery();

  const prepMsg = t("Speichere Labore ...");
  const doneMsg = t("Labore");

  const prepare = async () => {
    const result = await load({});
    if (result.error) throw result.error;

    addStale("laboratories");

    setSynced((prev) => [...prev, prepMsg]);

    await Promise.all(
      (result.data!.labs ?? []).map(async (item) => {
        await documentDbPut<Laboratory>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "laboratories",
          item,
        );
      }),
    );

    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  return { prepare };
}

export function useGetLaboratories(): Query<Laboratory[]> {
  const { offlineState } = useOfflineContext();
  const { workOrderId } = useParams();

  const {
    data: laboratoryList,
    isLoading: laboratoryIsLoading,
    error: laboratoryError,
  } = useSamplesLaboratoryListQuery({});
  const laboratories = laboratoryList?.labs ?? [];

  const {
    data: indexedDBData,
    isLoading: indexedDBIsLoading,
    error: indexedDBError,
  } = useDocumentDBGetAll<Laboratory>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "laboratories",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: laboratories,
        isLoading: laboratoryIsLoading,
        error: laboratoryError,
      };
    case OfflineState.OFFLINE:
      return {
        data: indexedDBData,
        isLoading: indexedDBIsLoading,
        error: indexedDBError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}
